<template>
  <div
    class="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800"
  >
    <HeaderDefault />

    <main
      class="mx-auto px-4 py-12 md:py-16 lg:py-20"
      :class="[
        // Use currentPost safely
        currentPost?.id === 1 ? 'max-w-screen-2xl' : 'max-w-4xl',
      ]"
    >
      <div
        v-if="isLoading"
        class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden animate-pulse"
      >
        <div class="h-[350px] md:h-[450px] bg-gray-200 dark:bg-gray-700"></div>
        <!-- Adjusted height -->
        <div class="p-6 md:p-10 space-y-6">
          <!-- Increased padding -->
          <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <!-- Adjusted height -->
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <!-- Adjusted width -->
          <div class="space-y-3 mt-6">
            <!-- Adjusted spacing -->
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>

      <!-- Error State from Store -->
      <div
        v-else-if="error"
        class="text-center py-20 px-6 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-700/50 max-w-2xl mx-auto shadow-sm"
      >
        <Icon
          name="heroicons:exclamation-triangle"
          class="text-5xl text-red-400 dark:text-red-500 mx-auto mb-5"
        />
        <!-- Replaced emoji with Icon -->
        <h3 class="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">
          Oops! Could not load post.
        </h3>
        <p class="text-red-600 dark:text-red-400 mb-6">{{ error }}</p>
        <!-- Increased margin-bottom -->
        <div class="flex justify-center gap-4">
          <!-- Added flex container for buttons -->
          <button
            @click="fetchPostData"
            class="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 focus:ring-offset-red-50 dark:focus:ring-offset-gray-900"
          >
            <Icon name="heroicons:arrow-path" class="h-5 w-5 mr-2" />
            <!-- Replaced SVG with Icon -->
            Try Again
          </button>
          <NuxtLink
            to="/buzz"
            class="inline-flex items-center px-5 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:ring-offset-red-50 dark:focus:ring-offset-gray-900"
          >
            Back to Posts
          </NuxtLink>
        </div>
      </div>

      <!-- Blog Content - Use currentPost from store -->
      <article
        v-else-if="currentPost"
        :class="[
          'bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden',
          currentPost.id === 1 ? 'grid md:grid-cols-2 gap-0 items-start' : '', // Added items-start for grid alignment
        ]"
      >
        <!-- Cover Image -->
        <div
          :class="[
            'relative',
            currentPost.id === 1
              ? 'h-full min-h-[400px] md:min-h-[600px]'
              : 'h-[300px] md:h-[400px]',
          ]"
        >
          <img
            :src="
              currentPost.attributes.cover?.data?.attributes?.url ||
              '/images/placeholder-blog.jpg'
            "
            :alt="currentPost.attributes.title"
            class="w-full h-full object-cover"
          />
          <!-- Categories Overlay -->
          <div
            class="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex flex-wrap gap-2"
          >
            <!-- Adjusted positioning and added flex-wrap -->
            <NuxtLink
              v-for="category in currentPost.attributes.categories?.data"
              :key="category.id"
              :to="`/buzz?category=${category.attributes.slug}`"
              class="px-2.5 py-1 bg-white/80 dark:bg-gray-900/70 backdrop-blur text-primary dark:text-primary-light rounded-full text-xs sm:text-sm font-medium shadow-sm hover:bg-white dark:hover:bg-gray-900 transition-colors"
            >
              {{ category.attributes.name }}
            </NuxtLink>
          </div>
        </div>

        <div class="p-6 md:p-8 lg:p-10">
          <!-- Increased padding -->
          <!-- Post Header -->
          <header
            :class="[
              'border-b border-gray-200 dark:border-gray-700 pb-6 md:pb-8', // Slightly darker border
              currentPost.id === 1 ? 'mb-6 md:mb-8' : 'mb-8 md:mb-10', // Increased margin-bottom
            ]"
          >
            <h1
              :class="[
                'font-bold text-gray-900 dark:text-white mb-3 leading-tight', // Adjusted margin, added leading-tight
                currentPost.id === 1
                  ? 'text-3xl md:text-4xl lg:text-5xl'
                  : 'text-3xl md:text-4xl', // Adjusted font sizes
              ]"
            >
              {{ currentPost.attributes.title }}
            </h1>
            <div
              class="flex flex-wrap items-center text-gray-500 dark:text-gray-400 text-sm gap-x-3 gap-y-1"
            >
              <!-- Added flex-wrap and gap -->
              <!-- Author Info -->
              <div
                v-if="currentPost.attributes.author?.data"
                class="flex items-center"
              >
                <img
                  v-if="
                    currentPost.attributes.author.data.attributes.avatar?.data
                      ?.attributes?.url
                  "
                  :src="
                    currentPost.attributes.author.data.attributes.avatar.data
                      .attributes.url
                  "
                  alt="Author Avatar"
                  class="w-6 h-6 rounded-full mr-2 border border-gray-200 dark:border-gray-600"
                />
                <!-- Added border -->
                <span
                  v-else
                  class="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 mr-2 inline-flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-300 border border-gray-300 dark:border-gray-500"
                >
                  <!-- Added border -->
                  {{
                    currentPost.attributes.author.data.attributes.name.charAt(0)
                  }}
                </span>
                <span>{{
                  currentPost.attributes.author.data.attributes.name
                }}</span>
              </div>
              <span
                v-if="currentPost.attributes.author?.data"
                class="hidden sm:inline"
                >·</span
              >
              <!-- Separator -->
              <!-- Publish Date -->
              <div class="flex items-center">
                <Icon
                  name="heroicons:calendar-days"
                  class="w-4 h-4 mr-1.5 flex-shrink-0"
                />
                <!-- Updated icon -->
                <NuxtTime
                  :datetime="currentPost.attributes.publishedAt"
                  format="MMMM D, YYYY"
                />
              </div>
            </div>
          </header>

          <!-- Post Content -->
          <div
            :class="[
              'prose prose-lg prose-primary dark:prose-invert max-w-none', // Use prose-lg for larger base size
              // Removed specific text size classes as prose handles it
            ]"
          >
            <MDC
              v-if="currentPost.attributes.body"
              :value="currentPost.attributes.body"
            />
            <p v-else class="text-gray-500 dark:text-gray-400 italic">
              Content not available.
            </p>
            <!-- Added italic -->
          </div>

          <!-- Back Button -->
          <div
            class="mt-10 md:mt-12 pt-8 md:pt-10 border-t border-gray-200 dark:border-gray-700"
          >
            <!-- Adjusted spacing/border -->
            <NuxtLink
              to="/buzz"
              class="inline-flex items-center text-sm font-medium rounded-md px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-gray-800 group transition-colors"
            >
              <Icon
                name="heroicons:arrow-left-solid"
                class="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform"
              />
              Back to All Posts
            </NuxtLink>
          </div>
        </div>
      </article>

      <!-- Fallback if no post found and not loading/error -->
      <div
        v-else
        class="text-center py-20 px-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 max-w-lg mx-auto shadow-sm"
      >
        <!-- Adjusted styling -->
        <Icon
          name="heroicons:question-mark-circle"
          class="text-5xl text-gray-400 dark:text-gray-500 mx-auto mb-5"
        />
        <!-- Icon for not found -->
        <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Post Not Found
        </h3>
        <p class="text-gray-500 dark:text-gray-400 mb-6">
          Sorry, we couldn't find the post you're looking for.
        </p>
        <NuxtLink
          to="/buzz"
          class="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:ring-offset-gray-50 dark:focus:ring-offset-gray-900"
        >
          View All Posts
        </NuxtLink>
      </div>
    </main>

    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
import { useBuzzStore } from "~/stores/buzz";
import { storeToRefs } from "pinia";
import { onMounted, watch } from "vue";

// --- Interfaces (can be removed if defined globally or imported) ---
// interface Category { ... }
// interface BlogPost { ... } // Make sure this matches the store's BlogPost exactly

const route = useRoute();
const config = useRuntimeConfig();
const buzzStore = useBuzzStore();

// Get reactive state and actions from the store
const { currentPost, isLoading, error } = storeToRefs(buzzStore);
const { fetchPostById, fetchPostBySlug, clearError } = buzzStore; // Added clearError

// --- Fetching Logic ---
const fetchPostData = async () => {
  const postId = Number(route.params.id); // Assuming ID is always numeric
  const postSlug = Array.isArray(route.params.id)
    ? route.params.id[0]
    : route.params.id; // Handle potential array/string

  if (!isNaN(postId)) {
    await fetchPostById(postId);
  } else if (postSlug) {
    // Fallback or primary method: fetch by slug if ID is not numeric or not preferred
    await fetchPostBySlug(postSlug);
  } else {
    console.error("No valid ID or slug found in route parameters.");
    // Optionally set an error state in the store here
  }
};

// Fetch data when component mounts
onMounted(() => {
  clearError(); // Clear previous errors before fetching
  fetchPostData();
});

// Optional: Refetch if the route parameter changes (e.g., navigating between posts)
// watch(() => route.params.id, () => {
//   clearError();
//   fetchPostData();
// });

// --- Metadata and Schema ---
// Use watch to react to changes in currentPost for metadata updates
watch(
  currentPost,
  (newPost) => {
    if (newPost) {
      useHead(() => {
        const article = newPost; // Use the structure from the store
        const coverUrl = article.attributes.cover?.data?.attributes?.url;
        // Generate description safely, handle potentially missing body
        const description = article.attributes.body
          ? article.attributes.body.substring(0, 160) + "..."
          : "Read the latest post from Academic Alerts.";

        return {
          title: `${article.attributes.title} - Academic Alerts Blog`,
          meta: [
            { name: "description", content: description },
            // Open Graph
            { property: "og:title", content: article.attributes.title },
            { property: "og:description", content: description },
            { property: "og:type", content: "article" },
            {
              property: "og:image",
              content: coverUrl || "/images/placeholder-blog.jpg",
            },
            {
              property: "og:url",
              content: `${config.public.siteUrl}/buzz/${article.id}`, // Use ID from store post
            },
            // Twitter Card
            { name: "twitter:card", content: "summary_large_image" },
            { name: "twitter:title", content: article.attributes.title },
            { name: "twitter:description", content: description },
            {
              name: "twitter:image",
              content: coverUrl || "/images/placeholder-blog.jpg",
            },
            // Article specific
            {
              property: "article:published_time",
              content: article.attributes.publishedAt, // Use publishedAt
            },
            {
              property: "article:section",
              content:
                article.attributes.categories?.data[0]?.attributes.name ||
                "Blog",
            },
            // Add author meta if available
            ...(article.attributes.author?.data
              ? [
                  {
                    property: "article:author",
                    content: article.attributes.author.data.attributes.name,
                  },
                ]
              : []),
          ],
          script: [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                headline: article.attributes.title,
                image: coverUrl || "/images/placeholder-blog.jpg",
                datePublished: article.attributes.publishedAt, // Use publishedAt
                dateModified: article.attributes.updatedAt, // Add dateModified
                articleSection:
                  article.attributes.categories?.data[0]?.attributes.name,
                description: description,
                author: {
                  // Use author data from store if available
                  "@type": article.attributes.author?.data
                    ? "Person"
                    : "Organization",
                  name: article.attributes.author?.data
                    ? article.attributes.author.data.attributes.name
                    : "Academic Alerts",
                },
                publisher: {
                  "@type": "Organization",
                  name: "Academic Alerts",
                  logo: {
                    "@type": "ImageObject",
                    url: `${config.public.siteUrl}/logo.png`, // Ensure this path is correct
                  },
                },
                mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": `${config.public.siteUrl}/buzz/${article.id}`, // Use ID from store post
                },
              }),
            },
          ],
        };
      });
    } else {
      // Set default/loading title if post is null
      useHead({ title: "Loading Post... - Academic Alerts Blog" });
    }
  },
  { immediate: true }
); // immediate: true ensures it runs on initial load
</script>

<style scoped>
.prose {
  max-width: none;
}

.prose img {
  border-radius: 0.75rem;
  margin: 2rem auto;
}

/* Enhanced MDC Typography */
:deep(.prose) {
  line-height: 1.8;
  color: #374151;
}

:deep(.prose h2) {
  font-size: 1.875rem;
  margin-top: 2.5rem;
  margin-bottom: 1.25rem;
  font-weight: 700;
  color: #111827;
}

:deep(.prose h3) {
  font-size: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #1f2937;
}

:deep(.prose p) {
  margin-bottom: 1.5rem;
}

:deep(.prose ul, .prose ol) {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

:deep(.prose li) {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

:deep(.prose blockquote) {
  font-style: italic;
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  margin: 1.5rem 0;
  color: #4b5563;
}

:deep(.prose code) {
  background-color: #f3f4f6;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  color: #1f2937;
}

:deep(.prose pre) {
  background-color: #1f2937;
  color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1.5rem 0;
}
/* Add dark mode prose styles if not already handled globally */
.dark .prose {
  color: #d1d5db; /* Example: text-gray-300 */
}
.dark .prose h1,
.dark .prose h2,
.dark .prose h3,
.dark .prose h4,
.dark .prose strong {
  color: #f9fafb; /* Example: text-gray-50 */
}
.dark .prose a {
  color: #93c5fd; /* Example: text-blue-300 */
}
</style>
